import RestoreIcon from '@mui/icons-material/Restore'
import { Box, Button, Card, CardContent, FormControl, FormHelperText, FormLabel, InputAdornment, Menu, MenuItem, Stack, TextField } from '@mui/material'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import React, { useState } from 'react'

import { LOCATIONS, SPORTS } from '../../share/Constant'
import { getSportProps } from '../../utils/utility'

const SPORT_TYPES = Object.values(SPORTS).map((sport) => getSportProps(sport))

const FormSectionLabel = ({ children, required = false }) => (
  <FormLabel
    sx={{
      mb: 2,
      display: 'block',
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.15rem',
      color: 'text.primary',
      '& .MuiFormLabel-asterisk': {
        color: 'error.main',
      },
    }}
    required={required}
  >
    {children}
  </FormLabel>
)

export default function PrivateSessionForm({ formData, formErrors, onFormChange, isEdit = false, template = {} }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleChange = (field, value) => {
    onFormChange({ ...formData, [field]: value })
  }

  // 打开模板选择菜单
  const handleOpenTemplateMenu = (event) => {
    setAnchorEl(event.currentTarget)
  }

  // 关闭模板选择菜单
  const handleCloseTemplateMenu = () => {
    setAnchorEl(null)
  }

  // 从模板加载数据到表单
  const handleLoadFromTemplate = (resortValue) => {
    if (!resortValue) {
      return
    }

    let templateData
    if (resortValue === 'default' && template.default) {
      templateData = template.default
    } else if (template[resortValue]) {
      templateData = template[resortValue]
    } else {
      return
    }

    const skiResort = templateData.skiResort === '' ? formData.skiResort : templateData.skiResort
    const sport = templateData.sport === '' ? formData.sport : templateData.sport

    onFormChange({
      ...formData,
      ...templateData,
      skiResort: skiResort,
      sport: sport,
    })
    handleCloseTemplateMenu()
  }

  // 获取所有可用的模板列表
  const availableTemplates = Object.keys(template)
    .map((resortValue) => {
      const location = LOCATIONS.find((loc) => loc.value === resortValue)
      return location ? { value: resortValue, label: location.label } : null
    })
    .filter((item) => item !== null)

  if (template.default) {
    availableTemplates.unshift({ value: 'default', label: 'Default Template' })
  }

  // 检查是否有可用的模板
  const hasTemplates = availableTemplates.length > 0

  return (
    <Card>
      <CardContent>
        {/* Sport Type Selection */}
        <FormControl fullWidth error={!!formErrors.sport} sx={{ mb: 2 }}>
          <FormSectionLabel required>Sport Type</FormSectionLabel>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
            {SPORT_TYPES.map((type) => (
              <Button
                key={type.value}
                variant={formData.sport === type.value ? 'contained' : 'outlined'}
                onClick={() => handleChange('sport', formData.sport === type.value ? '' : type.value)}
                startIcon={type.icon}
                sx={{
                  borderRadius: 8,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  fontSize: '0.875rem',
                  borderWidth: 1,
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                  },
                }}
              >
                {type.label}
              </Button>
            ))}
          </Stack>
          {formErrors.sport && <FormHelperText>{formErrors.sport}</FormHelperText>}
        </FormControl>

        {/* Location Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel required>Location</FormSectionLabel>
          <Card sx={{ p: 2, bgcolor: 'background.paper', border: 'none', boxShadow: 'none' }}>
            <FormControl fullWidth error={!!formErrors.skiResort}>
              <InputLabel>Select Resort</InputLabel>
              <Select value={formData.skiResort} label="Select Resort" onChange={(e) => handleChange('skiResort', e.target.value)}>
                {LOCATIONS.map((location) => (
                  <MenuItem key={location.value} value={location.value}>
                    {location.label}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.skiResort && <FormHelperText>{formErrors.skiResort}</FormHelperText>}
            </FormControl>
          </Card>
        </FormControl>

        {/* Session Details Form */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel>Session Details</FormSectionLabel>
          <Card sx={{ p: 0, bgcolor: 'background.paper', border: 'none', boxShadow: 'none' }}>
            <Box sx={{ '& .MuiTextField-root': { m: 1, width: '26ch' } }} noValidate autoComplete="off">
              {/* Basic Price */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <TextField
                  fullWidth
                  label="Basic Price"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  error={!!formErrors.price}
                  helperText={formErrors.price}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={{ width: '100% !important', mx: 1 }}
                />
              </Box>

              {/* Deadlines */}
              {!isEdit && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <TextField
                    label="Registration Deadline"
                    type="number"
                    value={formData.registrationDeadline}
                    onChange={(e) => handleChange('registrationDeadline', e.target.value)}
                    error={!!formErrors.registrationDeadline}
                    helperText={formErrors.registrationDeadline}
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">hours before</InputAdornment>,
                    }}
                  />

                  <TextField
                    label="Cancellation Deadline"
                    type="number"
                    value={formData.cancellationDeadline}
                    onChange={(e) => handleChange('cancellationDeadline', e.target.value)}
                    error={!!formErrors.cancellationDeadline}
                    helperText={formErrors.cancellationDeadline}
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">hours before</InputAdornment>,
                    }}
                  />
                </Box>
              )}
            </Box>
          </Card>
        </FormControl>

        {/* Session Description */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel>Private Session Description</FormSectionLabel>
          <TextField
            fullWidth
            multiline
            rows={1}
            placeholder="Add any additional information about the session..."
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                minHeight: '200px',
              },
              '& .MuiInputBase-inputMultiline': {
                minHeight: '180px !important',
              },
            }}
          />
        </FormControl>

        {/* Load from Template Button */}
        {hasTemplates && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={handleOpenTemplateMenu}
              sx={{
                textTransform: 'none',
              }}
            >
              Load from History
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleCloseTemplateMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {availableTemplates.map((templateOption) => (
                <MenuItem key={templateOption.value} onClick={() => handleLoadFromTemplate(templateOption.value)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RestoreIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    {templateOption.label}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
